import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  LayoutGrid,
  FileText,
  RefreshCw,
  Loader2,
  ChevronRight,
  ChevronDown,
  GripVertical,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  AlertTriangle,
} from 'lucide-react';

// CSS tokens now come from index.css — no local overrides needed
// Previously this file injected its own :root vars that overrode the global zinc palette
if (typeof document !== 'undefined' && document.getElementById('structure-tokens')) {
  document.getElementById('structure-tokens').remove(); // Clean up old injected styles
}
if (false) { // Dead code — kept for reference
  const style = document.createElement('style');
  style.id = 'structure-tokens';
  document.head.appendChild(style);
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Safely convert any AI-generated value to a renderable string.
 * The AI sometimes returns objects like {text, alt, href} instead of plain strings.
 * React error #31 = "Objects are not valid as a React child."
 */
function safeText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    // Common AI shapes: {text, label, name, value, title, content, description, src, alt}
    return value.text || value.label || value.name || value.title || value.content || value.value || value.description || value.alt || value.src || JSON.stringify(value);
  }
  return String(value);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const SECTION_TYPES = [
  // ── Hero / Intro ──────────────────────────────────────────────────────────
  { type: 'hero',         label: 'Hero',          color: 'bg-blue-500/20 text-blue-300 border-blue-500/30',          desc: 'Main banner / opening statement' },
  { type: 'about',        label: 'About',         color: 'bg-sky-500/20 text-sky-400 border-sky-500/30',             desc: 'About the company or founding story' },
  { type: 'video',        label: 'Video',         color: 'bg-blue-700/20 text-blue-400 border-blue-700/30',          desc: 'Video embed or background video hero' },

  // ── Features / Services ───────────────────────────────────────────────────
  { type: 'features',     label: 'Features',      color: 'bg-violet-500/20 text-violet-400 border-violet-500/30',    desc: 'List of key benefits or features' },
  { type: 'feature_grid', label: 'Feature Grid',  color: 'bg-violet-500/20 text-violet-400 border-violet-500/30',    desc: 'Grid layout of feature cards' },
  { type: 'services',     label: 'Services',      color: 'bg-purple-500/20 text-purple-400 border-purple-500/30',    desc: 'Services or offerings overview' },
  { type: 'comparison',   label: 'Comparison',    color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',    desc: 'Side-by-side feature comparison table' },
  { type: 'process',      label: 'Process',       color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',          desc: 'Step-by-step how-it-works / process' },
  { type: 'timeline',     label: 'Timeline',      color: 'bg-teal-500/20 text-teal-400 border-teal-500/30',          desc: 'Chronological journey or history' },

  // ── Social Proof / Trust ──────────────────────────────────────────────────
  { type: 'social_proof', label: 'Social Proof',  color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',       desc: 'Client logos / press mentions / badges' },
  { type: 'testimonials', label: 'Testimonials',  color: 'bg-amber-600/20 text-amber-300 border-amber-600/30',       desc: 'Customer quotes and reviews' },
  { type: 'case_study',   label: 'Case Study',    color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',    desc: 'In-depth customer success story' },
  { type: 'stats',        label: 'Stats',         color: 'bg-orange-500/20 text-orange-400 border-orange-500/30',    desc: 'Key metrics and numbers' },
  { type: 'awards',       label: 'Awards',        color: 'bg-yellow-600/20 text-yellow-300 border-yellow-600/30',    desc: 'Awards, certifications, recognition' },
  { type: 'partners',     label: 'Partners',      color: 'bg-orange-600/20 text-orange-300 border-orange-600/30',    desc: 'Partner or integration logos' },

  // ── Conversion ────────────────────────────────────────────────────────────
  { type: 'pricing',      label: 'Pricing',       color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', desc: 'Pricing tiers and plans' },
  { type: 'cta',          label: 'CTA',           color: 'bg-green-500/20 text-green-400 border-green-500/30',       desc: 'Call-to-action / conversion block' },
  { type: 'contact_form', label: 'Contact Form',  color: 'bg-green-700/20 text-green-400 border-green-700/30',       desc: 'Lead capture or contact form' },
  { type: 'newsletter',   label: 'Newsletter',    color: 'bg-lime-500/20 text-lime-400 border-lime-500/30',          desc: 'Email newsletter signup' },

  // ── Content ───────────────────────────────────────────────────────────────
  { type: 'faq',          label: 'FAQ',           color: 'bg-rose-500/20 text-rose-400 border-rose-500/30',          desc: 'Frequently asked questions' },
  { type: 'blog',         label: 'Blog',          color: 'bg-pink-500/20 text-pink-400 border-pink-500/30',          desc: 'Blog posts / articles grid' },
  { type: 'portfolio',    label: 'Portfolio',     color: 'bg-fuchsia-500/20 text-fuchsia-400 border-fuchsia-500/30', desc: 'Work portfolio / project showcase' },
  { type: 'gallery',      label: 'Gallery',       color: 'bg-pink-600/20 text-pink-300 border-pink-600/30',          desc: 'Image or video gallery' },

  // ── People ────────────────────────────────────────────────────────────────
  { type: 'team',         label: 'Team',          color: 'bg-red-500/20 text-red-400 border-red-500/30',             desc: 'Team members and bios' },

  // ── Utility ───────────────────────────────────────────────────────────────
  { type: 'content',      label: 'Content',       color: 'bg-zinc-700/40 text-zinc-400 border-zinc-600/30',          desc: 'Generic rich text / editorial block' },
  { type: 'footer',       label: 'Footer',        color: 'bg-zinc-700/40 text-zinc-400 border-zinc-600/30',          desc: 'Page footer with links' },
];

const TYPE_MAP = Object.fromEntries(SECTION_TYPES.map(t => [t.type, t]));

function getTypeColor(type) {
  return TYPE_MAP[type]?.color || 'bg-[var(--orbis-t4)]/20 text-[var(--orbis-t3)] border-[var(--orbis-s3)]/30';
}

// Strip the type-prefix from AI-generated section names ("Hero — Strategic Growth Promise" → "Strategic Growth Promise")
function getDescriptiveName(section) {
  const name = safeText(section.name);
  const parts = name.split(' — ');
  return parts.length > 1 ? parts.slice(1).join(' — ') : name;
}

// ─── Inline text editor ───────────────────────────────────────────────────────

function InlineEdit({ value, onSave, className = '' }) {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  const commit = () => { if (draft.trim()) onSave(draft.trim()); };
  const cancel = () => onSave(null); // null = cancel, restore original

  return (
    <form
      className="flex items-center gap-1"
      onSubmit={(e) => { e.preventDefault(); commit(); }}
    >
      <input
        ref={inputRef}
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => e.key === 'Escape' && cancel()}
        className={`bg-[var(--surface-2)] border-[0.5px] border-[var(--border-default)] rounded px-2 py-0.5 text-[var(--orbis-t1)] outline-none focus:border-[var(--border-active)] text-sm ${className}`}
      />
      <button type="submit" className="text-green-400 hover:text-green-300 p-0.5"><Check className="w-3.5 h-3.5" /></button>
      <button type="button" onClick={cancel} className="text-[var(--orbis-t4)] hover:text-[var(--orbis-t2)] p-0.5"><X className="w-3.5 h-3.5" /></button>
    </form>
  );
}

// ─── Section type picker ──────────────────────────────────────────────────────

function SectionTypePicker({ onSelect, onCancel }) {
  return (
    <div className="border-[0.5px] border-[var(--orbis-s2)] rounded-lg bg-[var(--surface-1)] p-3 space-y-2">
      <div className="flex items-center justify-between mb-1">
        <p style={{ fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.1em', fontWeight: 400, color: 'var(--orbis-t3)' }}>Choose section type</p>
        <button onClick={onCancel} className="text-[var(--orbis-t4)] hover:text-[var(--orbis-t2)]"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {SECTION_TYPES.map(({ type, label, color, desc }) => (
          <button
            key={type}
            onClick={() => onSelect(type, label)}
            className="flex flex-col items-start gap-0.5 p-2 rounded-lg border-[0.5px] border-transparent hover:border-[rgba(255,255,255,0.15)] hover:bg-[var(--surface-2)] transition-colors text-left"
          >
            <Badge variant="outline" className={`${color} text-[12px]`}>{label}</Badge>
            <span className="text-[12px] text-[var(--orbis-t2)] leading-tight">{desc}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Visual arc pills ─────────────────────────────────────────────────────────

function VisualArcPills({ arc }) {
  if (!arc) return null;
  const arcStr = safeText(arc);
  const steps = arcStr.split(' → ').map(s => s.trim()).filter(Boolean);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginTop: 4, width: '100%' }}>
      {steps.map((step, i) => (
        <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 100, background: 'var(--orbis-s2)', border: '0.5px solid var(--orbis-s3)', color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)', whiteSpace: 'nowrap' }}>
            {step}
          </span>
          {i < steps.length - 1 && <span style={{ fontSize: 10, color: 'var(--orbis-t4)' }}>→</span>}
        </span>
      ))}
    </div>
  );
}

// ─── Section flow pills ──────────────────────────────────────────────────────

function SectionFlowPills({ sections }) {
  if (!sections?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mt-2">
      {sections.map((section, i) => (
        <span key={section.id} className="flex items-center gap-1">
          <span className="px-2 py-0.5 rounded-full bg-[var(--surface-2)] border-[0.5px] border-[var(--orbis-s2)] text-[13px] text-[var(--orbis-t2)] whitespace-nowrap">
            {TYPE_MAP[section.type]?.label || section.type}
          </span>
          {i < sections.length - 1 && (
            <span className="text-[12px] text-[var(--orbis-t4)] select-none">&rarr;</span>
          )}
        </span>
      ))}
    </div>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────

function SectionCard({ section, isDragged, isDragOver, onDragStart, onDragOver, onDrop, onDragEnd, onRemove, onRenameStart }) {
  const color = getTypeColor(section.type);

  return (
    <motion.div
      layout
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      <div
        draggable
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        className={[
          'flex items-center gap-3 p-3 rounded-lg border-[0.5px] group transition-all select-none',
          isDragged  ? 'opacity-40 scale-95 bg-[var(--surface-1)]/30 border-[var(--orbis-s2)]' : 'bg-[var(--surface-1)] border-[var(--orbis-s2)]',
          isDragOver && !isDragged ? 'border-t-2 border-t-[var(--orbis-blue)] border-[var(--orbis-s2)]' : '',
        ].join(' ')}
      >
        <GripVertical className="w-4 h-4 text-[var(--orbis-t4)] cursor-grab active:cursor-grabbing shrink-0" />
        <Badge variant="outline" className={`${color} text-[12px] shrink-0`}>
          {TYPE_MAP[section.type]?.label || section.type.replace(/_/g, ' ')}
        </Badge>
        <div className="flex-1 min-w-0">
          <button
            onClick={onRenameStart}
            className="flex items-center gap-1 group/name text-left"
          >
            <p className="text-sm font-medium text-[var(--orbis-t1)] truncate">{safeText(section.name)}</p>
            <Pencil className="w-3 h-3 text-[var(--orbis-t4)] opacity-0 group-hover/name:opacity-100 transition-opacity shrink-0" />
          </button>
          <p className="text-[13px] text-[var(--orbis-t2)]">{safeText(section.description)}</p>
          {section.layout_pattern && (
            <p className="text-[13px] text-[var(--orbis-blue)]/70 mt-0.5">{safeText(section.layout_pattern)}</p>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="opacity-0 group-hover:opacity-100 text-[var(--orbis-t4)] hover:text-red-400 h-7 w-7 shrink-0"
          onClick={() => onRemove(section.id)}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </motion.div>
  );
}

// ─── Page accordion ───────────────────────────────────────────────────────────

function PageAccordion({
  page,
  isExpanded,
  onToggle,
  onRemoveSection,
  onAddSection,
  onReorderSections,
  onRenamePageStart,
  isRenamingPage,
  onRenamePageSave,
  onDeletePage,
  renamingSection,
  onRenameSectionStart,
  onRenameSectionSave,
}) {
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [showTypePicker, setShowTypePicker] = useState(false);

  const handleDragStart = (e, idx) => {
    setDraggedIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== dragOverIdx) setDragOverIdx(idx);
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (draggedIdx !== null && draggedIdx !== idx) {
      onReorderSections(page.id, draggedIdx, idx);
    }
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  const handleDragEnd = () => {
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  const handleSelectType = (type, label) => {
    onAddSection(page.id, type, label);
    setShowTypePicker(false);
  };

  return (
    <div
      className="border-[0.5px] border-[var(--orbis-s2)] rounded-xl overflow-hidden"
      aria-expanded={isExpanded}
    >
      {/* Header */}
      <div className="flex items-center bg-[var(--surface-1)] hover:bg-[var(--surface-2)] transition-colors">
        <button onClick={onToggle} className="flex-1 flex items-center gap-3 p-4 text-left">
          <FileText className="w-4 h-4 text-[var(--orbis-t2)] shrink-0" />
          <div className="flex-1 min-w-0">
            {isRenamingPage ? (
              <InlineEdit
                value={page.name}
                onSave={onRenamePageSave}
                className="text-sm font-medium w-40"
              />
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); onRenamePageStart(); }}
                className="flex items-center gap-1.5 group/pname text-left"
              >
                <h3 className="text-base font-medium text-[var(--orbis-t1)] truncate">{safeText(page.name)}</h3>
                <Pencil className="w-3 h-3 text-[var(--orbis-t4)] opacity-0 group-hover/pname:opacity-100 transition-opacity shrink-0" />
              </button>
            )}
            <p className="text-[13px] text-[var(--orbis-t2)] mt-0.5">{page.sections?.length || 0} sections</p>
            {page.page_goal && (
              <p className="text-[13px] text-[var(--orbis-t2)] mt-0.5">{safeText(page.page_goal)}</p>
            )}
            {page.visual_arc && (
              <p className="text-[13px] text-[var(--orbis-blue)]/60 mt-0.5">{safeText(page.visual_arc)}</p>
            )}
          </div>
          {page.is_homepage && (
            <Badge variant="outline" className="bg-[var(--orbis-blue)]/15 text-[var(--orbis-blue)] border-[var(--orbis-blue)]/30 text-[12px] shrink-0">
              Homepage
            </Badge>
          )}
        </button>
        <div className="flex items-center gap-1 pr-3">
          {!page.is_homepage && (
            <button
              onClick={(e) => { e.stopPropagation(); onDeletePage(page.id); }}
              className="p-1.5 text-[var(--orbis-t4)] hover:text-red-400 transition-colors rounded"
              title="Delete page"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={onToggle} className="p-1.5">
            <ChevronDown className={`w-4 h-4 text-[var(--orbis-t2)] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Collapsed: section flow pills */}
      {!isExpanded && page.sections?.length > 0 && (
        <div className="px-4 pb-3">
          <SectionFlowPills sections={page.sections} />
        </div>
      )}

      {/* Body */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="p-4 border-t border-[var(--orbis-s2)] space-y-2">
              {page.sections?.map((section, index) => (
                renamingSection?.sectionId === section.id ? (
                  <div key={section.id} className="flex items-center gap-3 p-3 bg-[var(--surface-1)] rounded-lg border-[0.5px] border-[rgba(184,204,223,0.35)]">
                    <Badge variant="outline" className={`${getTypeColor(section.type)} text-[12px] shrink-0`}>
                      {TYPE_MAP[section.type]?.label || section.type}
                    </Badge>
                    <InlineEdit
                      value={section.name}
                      onSave={(name) => onRenameSectionSave(page.id, section.id, name)}
                      className="text-sm flex-1"
                    />
                  </div>
                ) : (
                  <SectionCard
                    key={section.id}
                    section={section}
                    isDragged={draggedIdx === index}
                    isDragOver={dragOverIdx === index}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    onRemove={(sectionId) => onRemoveSection(page.id, sectionId)}
                    onRenameStart={() => onRenameSectionStart(page.id, section.id, section.name)}
                  />
                )
              ))}

              {showTypePicker ? (
                <SectionTypePicker
                  onSelect={handleSelectType}
                  onCancel={() => setShowTypePicker(false)}
                />
              ) : (
                <Button
                  variant="ghost"
                  className="w-full h-9 text-[var(--orbis-t2)] hover:text-[rgba(240,239,232,0.7)] border-[0.5px] border-dashed border-[var(--orbis-s2)] hover:border-[rgba(255,255,255,0.15)] text-sm rounded-[100px]"
                  onClick={() => setShowTypePicker(true)}
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  Add Section
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Right Panel: Sitemap Tree ───────────────────────────────────────────────

function SitemapTree({ pages }) {
  if (!pages?.length) return null;
  return (
    <div className="relative pl-4">
      {/* Vertical connector line */}
      <div className="absolute left-[7px] top-2 bottom-2 w-px bg-[var(--border-default)]" />
      <div className="space-y-2">
        {pages.map((page) => (
          <div key={page.id} className="relative flex items-center gap-2">
            {/* Horizontal connector */}
            <div className="absolute left-[-9px] top-1/2 w-2 h-px bg-[var(--border-default)]" />
            <div
              className={`w-5 h-5 rounded flex items-center justify-center shrink-0 border text-[11px] font-mono ${
                page.is_homepage
                  ? 'bg-[var(--surface-3)] border-[var(--border-active)]'
                  : 'bg-[var(--surface-3)] border-[var(--border-default)]'
              }`}
            >
              {page.is_homepage ? '/' : ''}
            </div>
            <span className="text-[13px] text-[var(--orbis-t2)] truncate">{safeText(page.name)}</span>
            <span className="text-[12px] text-[var(--orbis-t4)] ml-auto font-mono">{page.sections?.length || 0}s</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Right Panel: Navigation Preview ─────────────────────────────────────────

function NavigationPreview({ header, navigation }) {
  return (
    <div className="bg-[var(--orbis-bg)] border border-[var(--orbis-s2)] rounded-lg p-3.5">
      <div className="flex items-center gap-4">
        {/* Brand */}
        <span className="text-[14px] font-semibold text-[var(--orbis-t1)] shrink-0">{safeText(header?.logo) || 'Brand'}</span>
        {/* Nav links */}
        <div className="flex-1 flex items-center justify-end gap-3 flex-wrap">
          {navigation?.map((item, i) => (
            <span key={i} className="text-[13px] text-[var(--orbis-t2)] hover:text-[var(--orbis-t1)] transition-colors cursor-default">
              {safeText(typeof item === 'string' ? item : item.label)}
            </span>
          ))}
        </div>
        {/* CTA */}
        <span className="text-[12px] px-3 py-1.5 rounded-full bg-[var(--orbis-t1)] text-[var(--orbis-bg)] font-medium shrink-0">
          {safeText(header?.cta_button?.label) || 'Get Started'}
        </span>
      </div>
    </div>
  );
}

// ─── Right Panel: CTA Strategy ───────────────────────────────────────────────

function CTAStrategyCard({ ctaStrategy }) {
  if (!ctaStrategy) return null;
  const isObject = typeof ctaStrategy === 'object';
  return (
    <div className="space-y-2">
      {isObject ? (
        <>
          {ctaStrategy.primary_action && (
            <div className="flex items-baseline gap-3">
              <span className="text-[11px] text-[var(--orbis-t4)] shrink-0 w-16">Primary</span>
              <span className="text-[13px] font-medium text-[var(--orbis-t1)]">{safeText(ctaStrategy.primary_action)}</span>
            </div>
          )}
          {ctaStrategy.secondary_action && (
            <div className="flex items-baseline gap-3">
              <span className="text-[11px] text-[var(--orbis-t4)] shrink-0 w-16">Secondary</span>
              <span className="text-[13px] text-[var(--orbis-t1)]">{safeText(ctaStrategy.secondary_action)}</span>
            </div>
          )}
          {ctaStrategy.placement_logic && (
            <p className="text-[12px] text-[var(--orbis-t2)] italic pt-1 border-t border-[var(--orbis-s2)]">{safeText(ctaStrategy.placement_logic)}</p>
          )}
          {ctaStrategy.description && (
            <p className="text-[12px] text-[var(--orbis-t2)] italic">{safeText(ctaStrategy.description)}</p>
          )}
        </>
      ) : (
        <p className="text-[13px] text-[var(--orbis-t1)]">{safeText(ctaStrategy)}</p>
      )}
    </div>
  );
}

// ─── Right Panel: Design Principles ──────────────────────────────────────────

function DesignPrinciplesList({ principles }) {
  if (!principles?.length) return null;
  return (
    <div className="space-y-0">
      {principles.map((principle, i) => (
        <div key={i} className="flex items-start gap-3 py-2.5 border-b border-[var(--orbis-s2)] last:border-b-0">
          <span className="text-[12px] font-mono text-[var(--orbis-s3)] mt-px shrink-0 w-4 text-right">{i + 1}</span>
          <span className="text-[13px] text-[var(--orbis-t1)] leading-relaxed">{safeText(principle)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Right Panel: Content Hierarchy ──────────────────────────────────────────

function ContentHierarchyPills({ contentHierarchy }) {
  if (!contentHierarchy) return null;
  const isObject = typeof contentHierarchy === 'object';

  if (!isObject) {
    return <p className="text-[13px] text-[var(--orbis-t1)]">{safeText(contentHierarchy)}</p>;
  }

  return (
    <div className="space-y-3">
      {contentHierarchy.flow && (
        <p className="text-[13px] text-[var(--orbis-t1)]">{safeText(contentHierarchy.flow)}</p>
      )}
      {contentHierarchy.emphasis_points?.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {contentHierarchy.emphasis_points.map((point, i) => (
            <span key={i} className="flex items-center gap-1.5">
              <span className="px-2.5 py-1 rounded-full bg-[var(--orbis-s2)] border-[0.5px] border-[var(--orbis-s3)] text-[12px] text-[var(--orbis-t2)] whitespace-nowrap">
                {safeText(point)}
              </span>
              {i < contentHierarchy.emphasis_points.length - 1 && (
                <span className="text-[12px] text-[var(--orbis-s3)] select-none">&rarr;</span>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Right Panel Section Header ──────────────────────────────────────────────

function PanelSectionHeader({ children }) {
  return (
    <h3 style={{ fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.1em', fontWeight: 400, color: 'var(--orbis-t3)' }} className="mb-3">
      {children}
    </h3>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StructureStage({ structure, brief, brandContext, onRegenerate, onApprove, projectId, fallbackReason, onAcceptFallback }) {
  const [localStructure, setLocalStructure] = useState(structure);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [expandedPages, setExpandedPages] = useState([]);

  // Rename state
  const [renamingPageId, setRenamingPageId] = useState(null);
  const [renamingSection, setRenamingSection] = useState(null); // { pageId, sectionId }
  const [rightPanelView, setRightPanelView] = useState('wireframe'); // 'wireframe' | 'strategy'
  const [wireframePageId, setWireframePageId] = useState(null); // active page in wireframe view
  const [activeSectionId, setActiveSectionId] = useState(null); // hovered section — drives wireframe highlight
  const [addingSectionPageId, setAddingSectionPageId] = useState(null); // page showing the type picker

  // Sync structure prop → localStructure when parent updates it
  // (fixes black screen when component mounts before structure data arrives)
  useEffect(() => {
    if (structure) setLocalStructure(structure);
  }, [structure]);

  // When hovering a section on the left, auto-switch wireframe to its page
  useEffect(() => {
    if (!activeSectionId) return;
    const page = localStructure.pages?.find(p => p.sections?.some(s => s.id === activeSectionId));
    if (page && page.id !== wireframePageId) setWireframePageId(page.id);
  }, [activeSectionId]);

  const prefersReducedMotion = useReducedMotion();

  if (!localStructure) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[var(--orbis-t4)] animate-spin mx-auto mb-4" />
          <p className="text-[var(--orbis-t2)]">Generating structure...</p>
        </div>
      </div>
    );
  }

  // ── Page actions ────────────────────────────────────────────────────────────

  const togglePage = (pageId) => {
    setExpandedPages(prev =>
      prev.includes(pageId) ? prev.filter(id => id !== pageId) : [...prev, pageId]
    );
  };

  const handleAddPage = () => {
    const id = `page_${Date.now()}`;
    const slug = `/new-page`;
    const name = 'New Page';
    const newPage = { id, name, slug, is_homepage: false, sections: [] };
    setLocalStructure(prev => ({
      ...prev,
      pages: [...(prev.pages || []), newPage],
      sitemap: [...(prev.sitemap || []), slug],
      navigation: [...(prev.navigation || []), { label: name, href: slug }]
    }));
    setExpandedPages(prev => [...prev, id]);
    setRenamingPageId(id);
  };

  const handleDeletePage = (pageId) => {
    const page = localStructure.pages?.find(p => p.id === pageId);
    if (!page) return;
    setLocalStructure(prev => ({
      ...prev,
      pages: prev.pages.filter(p => p.id !== pageId),
      sitemap: prev.sitemap?.filter(s => s !== page.slug),
      navigation: prev.navigation?.filter(n => n.href !== page.slug)
    }));
    setExpandedPages(prev => prev.filter(id => id !== pageId));
  };

  const handleRenamePageSave = (pageId, newName) => {
    setRenamingPageId(null);
    if (!newName) return;
    const page = localStructure.pages?.find(p => p.id === pageId);
    setLocalStructure(prev => ({
      ...prev,
      pages: prev.pages.map(p => p.id === pageId ? { ...p, name: newName } : p),
      navigation: prev.navigation?.map(n =>
        page && n.href === page.slug ? { ...n, label: newName } : n
      )
    }));
  };

  // ── Section actions ─────────────────────────────────────────────────────────

  const handleAddSection = (pageId, type, label) => {
    const newSection = {
      id: `section_${Date.now()}`,
      type,
      name: label,
      description: TYPE_MAP[type]?.desc || ''
    };
    setLocalStructure(prev => ({
      ...prev,
      pages: prev.pages.map(p =>
        p.id === pageId ? { ...p, sections: [...(p.sections || []), newSection] } : p
      )
    }));
  };

  const handleRemoveSection = (pageId, sectionId) => {
    setLocalStructure(prev => ({
      ...prev,
      pages: prev.pages.map(p =>
        p.id === pageId ? { ...p, sections: p.sections.filter(s => s.id !== sectionId) } : p
      )
    }));
  };

  const handleReorderSections = (pageId, fromIdx, toIdx) => {
    setLocalStructure(prev => ({
      ...prev,
      pages: prev.pages.map(p => {
        if (p.id !== pageId) return p;
        const sections = [...p.sections];
        const [moved] = sections.splice(fromIdx, 1);
        sections.splice(toIdx, 0, moved);
        return { ...p, sections };
      })
    }));
  };

  const handleRenameSectionSave = (pageId, sectionId, newName) => {
    setRenamingSection(null);
    if (!newName) return;
    setLocalStructure(prev => ({
      ...prev,
      pages: prev.pages.map(p =>
        p.id === pageId
          ? { ...p, sections: p.sections.map(s => s.id === sectionId ? { ...s, name: newName } : s) }
          : p
      )
    }));
  };

  // ── Stage actions ───────────────────────────────────────────────────────────

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      await onRegenerate();
      setLocalStructure(structure);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      // Save local edits to backend before advancing to content stage
      if (localStructure && projectId) {
        try {
          await axios.post(`${API_URL}/magic/projects/${projectId}/edit-structure`, {
            structure: localStructure
          });
        } catch (editError) {
          console.error('edit-structure failed:', editError.response?.data || editError.message);
          toast.error(editError.response?.data?.detail || 'Failed to save structure edits');
          setIsApproving(false);
          return; // Don't proceed to content generation if save fails
        }
      }
      await onApprove();
    } catch (err) {
      console.error('Approve flow error:', err.response?.data || err.message);
      toast.error(err.response?.data?.detail || 'Failed to proceed');
    } finally {
      setIsApproving(false);
    }
  };

  // ── Animation variants ────────────────────────────────────────────────────

  const staggerContainer = {
    hidden: {},
    show: { transition: { staggerChildren: prefersReducedMotion ? 0 : 0.06 } }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 12 },
    show: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col w-full" style={{ height: 'calc(100vh - 64px)', background: 'var(--surface-0)' }}>

      {/* Two-panel split: 40% structure editor + 60% wireframe/strategy */}
      <div className="flex flex-1 min-h-0">

        {/* ── LEFT PANEL: Structure Editor ── */}
        <div className="w-[480px] shrink-0 overflow-y-auto flex flex-col" style={{ borderRight: '1px solid #27272A60', padding: 20, gap: 12 }}>
          {/* Header */}
          <div className="flex items-center justify-between">
            <span style={{ fontSize: 18, fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}>Site Structure</span>
            <div className="flex items-center gap-2">
              <button
                className="flex items-center gap-1.5 rounded-md transition-colors"
                style={{ padding: '7px 12px', background: 'var(--orbis-s2)', border: '0.5px solid var(--orbis-s3)', fontSize: 12, fontWeight: 500, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)' }}
                onClick={handleRegenerate}
                disabled={isRegenerating}
              >
                <RefreshCw className="w-3.5 h-3.5" />
                {isRegenerating ? 'Regenerating...' : 'AI Suggest'}
              </button>
              <button
                className="flex items-center gap-1.5 rounded-md transition-colors"
                style={{ padding: '7px 12px', background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)', fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}
                onClick={handleAddPage}
              >
                <Plus className="w-3.5 h-3.5" />
                Add Page
              </button>
            </div>
          </div>

          {/* Page goal cards + compact section rows */}
          <motion.div
            className="space-y-3"
            variants={staggerContainer}
            initial="hidden"
            animate="show"
          >
            {localStructure.pages?.map((page, pageIdx) => {
              const isExpanded = expandedPages.includes(page.id);
              const isHome = page.slug === 'home' || page.slug === '' || page.slug === 'index';
              const sectionCount = (page.sections || []).length;

              return (
                <motion.div key={page.id} variants={staggerItem}>
                  {/* Page goal header card */}
                  <div className="rounded-lg" style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-s2)', overflow: 'hidden' }}>
                    {/* Page header row */}
                    <button
                      onClick={() => togglePage(page.id)}
                      className="w-full flex items-center justify-between p-4 transition-colors hover:bg-[#FAFAFA04]"
                      style={{ borderBottom: isExpanded ? '1px solid var(--orbis-s2)' : 'none' }}
                    >
                      <div className="flex items-center gap-3">
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`} style={{ color: 'var(--orbis-t3)' }} />
                        <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}>{safeText(page.name)}</span>
                        {isHome && (
                          <span className="rounded-full" style={{ padding: '3px 10px', background: 'var(--orbis-s2)', border: '0.5px solid var(--orbis-s3)', fontSize: 10, fontWeight: 500, letterSpacing: 0.5, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-mono)' }}>
                            Homepage
                          </span>
                        )}
                      </div>
                      <span style={{ fontSize: 11, padding: '2px 9px', borderRadius: 100, background: 'var(--orbis-s2)', border: '0.5px solid var(--orbis-s3)', color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)' }}>
                        {sectionCount} sections
                      </span>
                    </button>

                    {/* Page goal */}
                    {isExpanded && page.page_goal && (
                      <div className="px-5 py-3" style={{ borderBottom: '1px solid var(--orbis-s2)' }}>
                        <span style={{ fontSize: 10, letterSpacing: '0.08em', color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase' }}>Goal</span>
                        <p style={{ fontSize: 13, fontStyle: 'italic', color: 'var(--orbis-t2)', lineHeight: 1.5, marginTop: 2, fontFamily: 'var(--orbis-font)' }}>
                          {safeText(page.page_goal)}
                        </p>
                      </div>
                    )}

                    {/* Compact section rows */}
                    {isExpanded && (
                      <div className="p-2.5 space-y-0.5">
                        {(page.sections || []).map((section, idx) => {
                          const typeColor = getTypeColor(section.type);
                          const typeLabel = TYPE_MAP[section.type]?.label || section.type.replace(/_/g, ' ');
                          const descriptiveName = getDescriptiveName(section);
                          if (renamingSection?.sectionId === section.id) {
                            return (
                              <div key={section.id || idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--orbis-s1)', borderRadius: 6, border: '0.5px solid rgba(184,204,223,0.35)' }}>
                                <span className={`text-[11px] font-medium px-2 py-0.5 rounded shrink-0 ${typeColor}`}>{typeLabel}</span>
                                <InlineEdit
                                  value={section.name}
                                  onSave={(name) => handleRenameSectionSave(page.id, section.id, name)}
                                  className="text-sm flex-1"
                                />
                              </div>
                            );
                          }
                          return (
                            <div
                              key={section.id || idx}
                              className="group flex items-center gap-2 rounded-md transition-colors hover:bg-[#FAFAFA08]"
                              style={{ minHeight: 34, padding: '5px 10px' }}
                              draggable
                              onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx)); }}
                              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                              onDrop={(e) => { e.preventDefault(); const from = parseInt(e.dataTransfer.getData('text/plain')); if (from !== idx) handleReorderSections(page.id, from, idx); }}
                              onMouseEnter={() => setActiveSectionId(section.id)}
                              onMouseLeave={() => setActiveSectionId(null)}
                            >
                              <GripVertical className="w-3.5 h-3.5 shrink-0 cursor-grab" style={{ color: 'var(--orbis-t4)' }} />
                              <span style={{ fontSize: 10, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)', width: 14, textAlign: 'right', flexShrink: 0, userSelect: 'none' }}>
                                {idx + 1}
                              </span>
                              <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded shrink-0 ${typeColor}`}>
                                {typeLabel}
                              </span>
                              <span className="flex-1 min-w-0 truncate" style={{ fontSize: 12, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)' }}>
                                {descriptiveName}
                              </span>
                              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                <button
                                  onClick={() => setRenamingSection({ pageId: page.id, sectionId: section.id })}
                                  className="p-1 rounded hover:bg-[#FAFAFA10] transition-colors"
                                  style={{ color: 'var(--orbis-t4)' }}
                                  title="Rename"
                                >
                                  <Pencil className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleRemoveSection(page.id, section.id)}
                                  className="p-1 rounded hover:bg-[#FAFAFA10] hover:text-red-400 transition-colors"
                                  style={{ color: 'var(--orbis-t4)' }}
                                  title="Delete"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          );
                        })}

                        {/* Footer — always present, part of layout shell */}
                        <div
                          className="flex items-center gap-2 rounded-md"
                          style={{ minHeight: 34, padding: '5px 10px', opacity: 0.5 }}
                        >
                          <GripVertical className="w-3.5 h-3.5 shrink-0 invisible" />
                          <span style={{ fontSize: 10, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)', width: 14, textAlign: 'right', flexShrink: 0, userSelect: 'none' }}>
                            ↓
                          </span>
                          <span className="text-[11px] font-medium px-1.5 py-0.5 rounded shrink-0 bg-zinc-800 text-zinc-400">
                            Footer
                          </span>
                          <span className="flex-1 min-w-0 truncate" style={{ fontSize: 12, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)', fontStyle: 'italic' }}>
                            Auto-generated from layout shell
                          </span>
                        </div>

                        {/* Add section */}
                        {addingSectionPageId === page.id ? (
                          <SectionTypePicker
                            onSelect={(type, label) => {
                              handleAddSection(page.id, type, label);
                              setAddingSectionPageId(null);
                            }}
                            onCancel={() => setAddingSectionPageId(null)}
                          />
                        ) : (
                          <button
                            onClick={() => setAddingSectionPageId(page.id)}
                            className="w-full flex items-center justify-center gap-2 rounded-md transition-colors hover:bg-[#FAFAFA06]"
                            style={{ height: 34, marginTop: 4, border: '1px dashed #27272A60', fontSize: 12, color: 'var(--orbis-s3)', fontFamily: 'var(--orbis-font)' }}
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Add Section
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

        </div>

        {/* ── RIGHT PANEL: Wireframe / Strategy Toggle ── */}
        <div className="flex-1 overflow-y-auto flex flex-col" style={{ background: '#0E0E10', padding: 20 }}>
          {/* Toggle header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-0.5 rounded-lg p-0.5" style={{ background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)' }}>
              <button
                onClick={() => setRightPanelView('wireframe')}
                className="rounded-md transition-colors"
                style={{
                  padding: '6px 14px', fontSize: 12, fontWeight: rightPanelView === 'wireframe' ? 500 : 400,
                  background: rightPanelView === 'wireframe' ? 'rgba(250,250,250,0.06)' : 'transparent',
                  color: rightPanelView === 'wireframe' ? 'var(--orbis-t1)' : 'var(--orbis-t3)',
                  fontFamily: 'var(--orbis-font)',
                }}
              >
                Wireframe
              </button>
              <button
                onClick={() => setRightPanelView('strategy')}
                className="rounded-md transition-colors"
                style={{
                  padding: '6px 14px', fontSize: 12, fontWeight: rightPanelView === 'strategy' ? 500 : 400,
                  background: rightPanelView === 'strategy' ? 'rgba(250,250,250,0.06)' : 'transparent',
                  color: rightPanelView === 'strategy' ? 'var(--orbis-t1)' : 'var(--orbis-t3)',
                  fontFamily: 'var(--orbis-font)',
                }}
              >
                Strategy
              </button>
            </div>
            <span style={{ fontSize: 11, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)' }}>
              {rightPanelView === 'wireframe' ? 'Abstract layout · Not final design' : 'AI-generated strategic context'}
            </span>
          </div>

          {/* Strategy view */}
          {rightPanelView === 'strategy' && (
            <div className="space-y-4">
              {/* Navigation */}
              <div className="rounded-lg p-4 space-y-3" style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-s2)' }}>
                <div>
                  <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: 1.5, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase' }}>Navigation</span>
                  <p style={{ fontSize: 11, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)', marginTop: 3 }}>How your header will be structured — logo, menu links, and the main action button visitors see first.</p>
                </div>
                <NavigationPreview header={localStructure.header} navigation={localStructure.navigation} />
              </div>

              {/* Visual Arc */}
              {localStructure.pages?.some(p => p.visual_arc) && (
                <div className="rounded-lg p-4 space-y-3" style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-s2)' }}>
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: 1.5, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase' }}>Emotional Arc</span>
                    <p style={{ fontSize: 11, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)', marginTop: 3 }}>The mood and atmosphere shift as visitors scroll — how the page should <em>feel</em> at each stage, independent of what it says.</p>
                  </div>
                  {localStructure.pages.filter(p => p.visual_arc).map(page => (
                    <div key={page.id}>
                      {localStructure.pages.filter(p => p.visual_arc).length > 1 && (
                        <span style={{ fontSize: 10, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)', display: 'block', marginBottom: 4 }}>{safeText(page.name)}</span>
                      )}
                      <VisualArcPills arc={page.visual_arc} />
                    </div>
                  ))}
                </div>
              )}

              {/* CTA Strategy */}
              {localStructure.cta_strategy && (
                <div className="rounded-lg p-4 space-y-3" style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-s2)' }}>
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: 1.5, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase' }}>CTA Strategy</span>
                    <p style={{ fontSize: 11, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)', marginTop: 3 }}>The primary and secondary actions you want visitors to take, and where they appear. Good CTAs are placed sparingly — earned by the content around them.</p>
                  </div>
                  <CTAStrategyCard ctaStrategy={localStructure.cta_strategy} />
                </div>
              )}

              {/* Content Flow */}
              {localStructure.content_hierarchy && (
                <div className="rounded-lg p-4 space-y-3" style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-s2)' }}>
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: 1.5, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase' }}>Content Flow</span>
                    <p style={{ fontSize: 11, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)', marginTop: 3 }}>The logical order of information — what visitors understand at each step as they read down the page. The highlighted moments below are the highest-stakes sections that carry the most conversion weight.</p>
                  </div>
                  <ContentHierarchyPills contentHierarchy={localStructure.content_hierarchy} />
                </div>
              )}

              {/* Design Principles */}
              {localStructure.design_principles?.length > 0 && (
                <div className="rounded-lg p-4 space-y-3" style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-s2)' }}>
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: 1.5, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase' }}>Design Principles</span>
                    <p style={{ fontSize: 11, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)', marginTop: 3 }}>Rules the AI applied when laying out this page. Use these as a checklist when reviewing the final build — if something feels off, one of these principles was likely violated.</p>
                  </div>
                  <DesignPrinciplesList principles={localStructure.design_principles} />
                </div>
              )}
            </div>
          )}

          {/* Wireframe view — page-aware with clickable nav */}
          {rightPanelView === 'wireframe' && (() => {
            const pages = localStructure.pages || [];
            const activePageId = wireframePageId || pages[0]?.id;
            const activePage = pages.find(p => p.id === activePageId) || pages[0];
            const activeSections = activePage?.sections || [];

            return (
              <div className="rounded-xl overflow-hidden flex flex-col flex-1" style={{ background: '#111113', border: '1px solid var(--orbis-s2)' }}>
                {/* Wireframe nav bar — clickable page links */}
                {(() => {
                  const isLanding = (brief?.generation_mode || 'landing_page') === 'landing_page';
                  const logoUrl = brandContext?.logo_url;
                  const companyName = safeText(localStructure.header?.logo) || brief?.company_name;
                  const ctaText = safeText(localStructure.header?.cta_button?.label) || safeText(localStructure.cta_strategy?.primary_action) || 'Get Started';
                  const navLinks = localStructure.navigation || [];
                  const navPages = isLanding ? [] : pages;
                  return (
                    <div className="flex items-center justify-between px-5 shrink-0" style={{ height: 40, background: 'var(--orbis-s1)', borderBottom: '1px solid var(--orbis-s2)' }}>
                      {/* Logo / company name */}
                      {logoUrl ? (
                        <img src={logoUrl} alt={companyName} style={{ height: 18, maxWidth: 80, objectFit: 'contain', opacity: 0.85 }} />
                      ) : companyName ? (
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)', letterSpacing: 0.3 }}>{companyName}</span>
                      ) : (
                        <div className="w-12 h-2 rounded-sm" style={{ background: '#FAFAFA20' }} />
                      )}
                      {/* Nav links — actual navigation items from structure */}
                      {navLinks.length > 0 ? (
                        <div className="flex items-center gap-3">
                          {navLinks.map((item, i) => (
                            <span key={i} style={{ fontSize: 10, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)', whiteSpace: 'nowrap' }}>
                              {safeText(typeof item === 'string' ? item : item.label)}
                            </span>
                          ))}
                        </div>
                      ) : navPages.length > 0 ? (
                        <div className="flex items-center gap-3">
                          {navPages.map(page => (
                            <button
                              key={page.id}
                              onClick={() => setWireframePageId(page.id)}
                              style={{ fontSize: 10, fontFamily: 'var(--orbis-font)', color: page.id === activePageId ? 'var(--orbis-t1)' : 'var(--orbis-t4)', background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              {safeText(page.name)}
                            </button>
                          ))}
                        </div>
                      ) : null}
                      {/* CTA button */}
                      <div className="rounded-full px-3 py-1" style={{ border: '0.5px solid #FAFAFA20' }}>
                        <span style={{ fontSize: 9, fontWeight: 400, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)', whiteSpace: 'nowrap' }}>{ctaText}</span>
                      </div>
                    </div>
                  );
                })()}

                {/* Active page sections — natural height, scrollable */}
                <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
                  {activeSections.map((section, idx) => {
                    const isHero = section.type === 'hero';
                    const isPricing = section.type === 'pricing';
                    const isCta = section.type === 'cta';
                    const isTestimonial = section.type === 'testimonials' || section.type === 'social_proof';
                    const isFaq = section.type === 'faq';
                    const isActive = activeSectionId === section.id;
                    // Fixed heights per section type so they stack naturally and are scrollable
                    const sectionHeight = isHero ? 220 : isPricing ? 240 : isCta ? 110 : isFaq ? 160 : isTestimonial ? 160 : 150;
                    return (
                      <div
                        key={section.id || idx}
                        className="flex flex-col gap-2 shrink-0"
                        style={{
                          height: sectionHeight,
                          background: isActive
                            ? (idx % 2 === 0 ? '#1A1E23' : '#171B1F')
                            : (idx % 2 === 0 ? '#141416' : '#111113'),
                          borderBottom: idx < activeSections.length - 1 ? '1px solid #27272A40' : 'none',
                          borderLeft: isActive ? '3px solid var(--orbis-blue)' : '3px solid transparent',
                          padding: '14px 40px',
                          paddingLeft: isActive ? 37 : 40,
                          justifyContent: 'center',
                          transition: 'background 0.15s ease, border-color 0.15s ease',
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                          <span className={`font-medium ${getTypeColor(section.type)}`} style={{ fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', padding: '2px 7px', borderRadius: 4, fontFamily: 'var(--orbis-mono)' }}>
                            {TYPE_MAP[section.type]?.label || section.type}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}>
                            {getDescriptiveName(section)}
                          </span>
                        </div>
                        {isHero && (
                          <div className="flex flex-col items-center gap-2.5 mt-3">
                            <div className="w-64 h-3 rounded-sm" style={{ background: '#FAFAFA18' }} />
                            <div className="w-44 h-2 rounded-sm" style={{ background: '#FAFAFA10' }} />
                            <div className="flex gap-2 mt-1">
                              <div className="w-20 h-5 rounded-full" style={{ background: '#B8CCDF20' }} />
                              <div className="w-20 h-5 rounded-full" style={{ background: '#FAFAFA08', border: '1px solid #FAFAFA15' }} />
                            </div>
                          </div>
                        )}
                        {!isHero && !isCta && !isFaq && (
                          <div className="flex gap-3 mt-2">
                            {[...Array(isPricing ? 3 : isTestimonial ? 3 : section.type === 'features' || section.type === 'feature_grid' || section.type === 'stats' ? 4 : 3)].map((_, i) => (
                              <div key={i} className="flex-1 rounded-md" style={{ height: isPricing ? 56 : 36, background: '#FAFAFA06', border: isPricing && i === 1 ? '1px solid #B8CCDF15' : 'none' }} />
                            ))}
                          </div>
                        )}
                        {isFaq && (
                          <div className="space-y-1.5 mt-2">
                            {[...Array(3)].map((_, i) => (
                              <div key={i} className="w-full rounded-md" style={{ height: 14, background: i === 0 ? '#FAFAFA06' : '#FAFAFA04' }} />
                            ))}
                          </div>
                        )}
                        {isCta && (
                          <div className="flex flex-col items-center gap-2 mt-2">
                            <div className="w-44 h-2.5 rounded-sm" style={{ background: '#FAFAFA15' }} />
                            <div className="w-24 h-5 rounded-full" style={{ background: '#B8CCDF18' }} />
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Footer — always last section, part of layout shell */}
                  <div
                    className="shrink-0 flex flex-col justify-center"
                    style={{
                      height: 120,
                      background: '#0E0E10',
                      borderTop: '1px solid #27272A60',
                      padding: '14px 40px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
                      <span className="bg-zinc-800 text-zinc-500" style={{ fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', padding: '2px 6px', borderRadius: 4, fontFamily: 'var(--orbis-mono)', fontWeight: 500 }}>
                        Footer
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-1.5">
                        <div className="w-12 h-1.5 rounded-sm" style={{ background: '#FAFAFA15' }} />
                        <div className="w-8 h-1 rounded-sm" style={{ background: '#FAFAFA08' }} />
                        <div className="w-8 h-1 rounded-sm" style={{ background: '#FAFAFA08' }} />
                        <div className="w-8 h-1 rounded-sm" style={{ background: '#FAFAFA08' }} />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <div className="w-10 h-1 rounded-sm" style={{ background: '#FAFAFA08' }} />
                        <div className="w-8 h-1 rounded-sm" style={{ background: '#FAFAFA06' }} />
                        <div className="w-8 h-1 rounded-sm" style={{ background: '#FAFAFA06' }} />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <div className="w-10 h-1 rounded-sm" style={{ background: '#FAFAFA08' }} />
                        <div className="w-8 h-1 rounded-sm" style={{ background: '#FAFAFA06' }} />
                        <div className="w-8 h-1 rounded-sm" style={{ background: '#FAFAFA06' }} />
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        <div className="flex gap-2">
                          <div className="w-3 h-3 rounded-sm" style={{ background: '#FAFAFA10' }} />
                          <div className="w-3 h-3 rounded-sm" style={{ background: '#FAFAFA10' }} />
                          <div className="w-3 h-3 rounded-sm" style={{ background: '#FAFAFA10' }} />
                        </div>
                        <div className="w-16 h-1 rounded-sm" style={{ background: '#FAFAFA06' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── FALLBACK BANNER ── */}
      {fallbackReason && (
        <div style={{
          position: 'fixed', bottom: '72px', left: '50%', transform: 'translateX(-50%)',
          zIndex: 90, maxWidth: '640px', width: '90%',
          background: 'rgba(180, 130, 50, 0.12)', backdropFilter: 'blur(12px)',
          border: '1px solid rgba(212, 184, 150, 0.25)', borderRadius: 'var(--orbis-r-lg)',
          padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px',
          fontFamily: 'var(--orbis-font)',
        }}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--orbis-amber)' }} />
          <div style={{ flex: 1, fontSize: '12px', color: 'var(--orbis-t2)', lineHeight: '1.5' }}>
            <span style={{ color: 'var(--orbis-amber)', fontWeight: 600 }}>Draft mode</span> — AI generation failed ({fallbackReason}). This is placeholder content.
          </div>
          <button
            onClick={handleRegenerate}
            disabled={isRegenerating}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
            style={{ background: 'var(--orbis-amber)', color: '#09090B', whiteSpace: 'nowrap' }}
          >
            <RefreshCw className={`w-3 h-3 ${isRegenerating ? 'animate-spin' : ''}`} />
            Retry with AI
          </button>
          <button
            onClick={onAcceptFallback}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs transition-colors"
            style={{ background: 'var(--orbis-s2)', color: 'var(--orbis-t3)', whiteSpace: 'nowrap' }}
          >
            Use draft
          </button>
        </div>
      )}

      {/* ── BOTTOM BAR ── */}
      <div className="magic-bottom-bar">
        <button
          onClick={handleRegenerate}
          disabled={isRegenerating}
          className="flex items-center justify-center gap-2 transition-colors rounded-full"
          style={{
            height: '44px', padding: '0 24px',
            background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)',
            fontSize: '12px', color: 'var(--orbis-t3)',
            cursor: isRegenerating ? 'not-allowed' : 'pointer',
            opacity: isRegenerating ? 0.5 : 1,
            fontFamily: 'var(--orbis-font)',
          }}
          data-testid="regenerate-structure-btn"
        >
          {isRegenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4" />Regenerate</>}
        </button>
        <span style={{ fontSize: '11px', color: 'var(--orbis-s3)', fontFamily: 'var(--orbis-font)', letterSpacing: '0.5px' }}>
          Structure · Step 3 of 8
        </span>
        <div className="orbis-cta-ring rounded-full p-0.5">
          <button
            onClick={handleApprove}
            disabled={isApproving || !!fallbackReason}
            className="orbis-cta flex items-center justify-center gap-2 transition-all rounded-full"
            style={{ height: '44px', padding: '0 28px', opacity: (isApproving || fallbackReason) ? 0.4 : 1, fontSize: '12px', fontWeight: 500, cursor: (isApproving || fallbackReason) ? 'not-allowed' : 'pointer', fontFamily: 'var(--orbis-font)' }}
            data-testid="approve-structure-btn"
          >
            {isApproving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" />Approve & Continue<ChevronRight className="w-4 h-4" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
